import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import * as React from 'react'
import { FC, useId, useMemo } from 'react'

export interface SortableRowContextValue {
    setActivatorNodeRef?: (element: HTMLElement | null) => void
    listeners?: SyntheticListenerMap
}

export const SortableRowContext = React.createContext<SortableRowContextValue>({})

type TableProps = React.HTMLAttributes<HTMLTableRowElement> & {
    'data-row-key'?: React.Key
}

type ListProps = React.HTMLAttributes<HTMLDivElement> & {
    id: string
}

export type SortableRowProps = TableProps | ListProps

/** Ant Design Table всегда кладёт data-row-key (иногда undefined у measure/expanded). */
const isTableProps = (props: SortableRowProps): props is TableProps =>
    'data-row-key' in props ||
    (typeof props.className === 'string' && /\bant-table-/.test(props.className))

/**
 * Универсальная sortable-строка:
 * - Ant Design Table: `components={{ body: { row: SortableRow } }}` (id из `data-row-key`)
 * - Список: `<SortableRow id={...}>...</SortableRow>` (обёртка-div)
 *
 * Ручка: `<SortableDragHandle />` внутри строки (через контекст).
 */
export const SortableRow: FC<SortableRowProps> = props => {
    const isTable = isTableProps(props)
    const fallbackId = useId()

    const rawId = isTable ? props['data-row-key'] : (props as ListProps).id
    const hasSortableId = rawId != null && String(rawId) !== ''
    const sortableId = hasSortableId ? String(rawId) : fallbackId

    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: sortableId, disabled: !hasSortableId })

    const style: React.CSSProperties = {
        ...props.style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 1000 } : {}),
    }

    const contextValue = useMemo<SortableRowContextValue>(
        () => ({ setActivatorNodeRef, listeners }),
        [setActivatorNodeRef, listeners]
    )

    return (
        <SortableRowContext.Provider value={contextValue}>
            {isTable ? (
                <tr {...props} ref={setNodeRef} style={style} {...attributes} />
            ) : (
                <div {...(props as ListProps)} ref={setNodeRef} style={style} {...attributes} />
            )}
        </SortableRowContext.Provider>
    )
}
